import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { MonoLabel } from "@/components/primitives";
import { DOCS } from "@/lib/docs-content";
import type { DocBlock } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useSEO } from "@/lib/useSEO";
import { PageShell } from "@/components/PageShell";

function Blocks({ blocks }: { blocks: DocBlock[] }) {
  return (
    <div className="space-y-5">
      {blocks.map((b, i) => {
        switch (b.type) {
          case "h3":
            return (
              <h3 key={i} className="pt-3 font-display text-lg font-bold uppercase tracking-wide text-ink">
                {b.text}
              </h3>
            );
          case "code":
            return (
              <div key={i} className="border border-line bg-base/80" data-testid="docs-code-block">
                <div className="border-b border-line/60 px-4 py-1.5">
                  <MonoLabel className="text-[8px]">{b.lang ?? "plaintext"}</MonoLabel>
                </div>
                <pre className="overflow-x-auto p-4 font-mono text-xs leading-relaxed text-cyan/90">
                  <code>{b.text}</code>
                </pre>
              </div>
            );
          case "list":
            return (
              <ul key={i} className="space-y-2.5">
                {b.items?.map((it, j) => (
                  <li key={j} className="flex gap-3 text-sm leading-relaxed text-dim">
                    <span className="mt-0.5 text-cyan">▸</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            );
          case "note":
            return (
              <div
                key={i}
                className={cn(
                  "border-l-2 bg-white/[0.02] px-5 py-4",
                  b.tone === "warn" ? "border-warn" : "border-cyan"
                )}
              >
                <MonoLabel className={cn("text-[8px]", b.tone === "warn" ? "text-warn" : "text-cyan")}>
                  {b.tone === "warn" ? "Important" : "Note"}
                </MonoLabel>
                <p className="mt-2 text-sm leading-relaxed text-dim">{b.text}</p>
              </div>
            );
          default:
            return (
              <p key={i} className="text-sm leading-relaxed text-dim sm:text-base">
                {b.text}
              </p>
            );
        }
      })}
    </div>
  );
}

export default function Docs() {
  useSEO({
    title: "Docs — Protocol Documentation | Consequence Protocol",
    description:
      "Documentation for Consequence Protocol: Proof of Consequence, Outcome Contracts, evidence, verification, the Consequence Score, disputes, and economics.",
    path: "/docs",
  });

  const location = useLocation();
  const initial = location.hash.replace("#", "");
  const [active, setActive] = useState(DOCS.some((d) => d.id === initial) ? initial : DOCS[0].id);

  useEffect(() => {
    window.history.replaceState(null, "", `#${active}`);
  }, [active]);

  const section = DOCS.find((d) => d.id === active) ?? DOCS[0];

  return (
    <PageShell testId="page-docs">
      <section className="container-t pb-28 pt-20 sm:pt-28">
        <Reveal>
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-cyan/50" />
            <MonoLabel>DOCS · CONCEPT v1.0</MonoLabel>
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold uppercase tracking-tight text-ink sm:text-4xl">
            Protocol Documentation
          </h1>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-[240px_1fr]">
          {/* Sidebar */}
          <aside>
            <nav aria-label="Documentation" className="flex gap-2 overflow-x-auto pb-2 lg:sticky lg:top-24 lg:flex-col lg:overflow-visible lg:pb-0">
              {DOCS.map((d) => (
                <button
                  key={d.id}
                  data-testid={`docs-nav-${d.id}`}
                  onClick={() => setActive(d.id)}
                  className={cn(
                    "shrink-0 border px-3 py-2 text-left font-mono text-[11px] uppercase tracking-[0.14em] transition-colors duration-150 lg:border-0 lg:border-l-2 lg:px-4",
                    active === d.id
                      ? "border-cyan/60 bg-cyan/10 text-cyan lg:border-cyan lg:bg-transparent"
                      : "border-line text-dim hover:text-ink lg:border-line/60"
                  )}
                >
                  {d.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <article className="panel scanlines hud-corners min-h-[60vh] p-6 sm:p-10" data-testid="docs-content">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-5">
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-ink sm:text-3xl">
                {section.title}
              </h2>
              <MonoLabel className="text-[8px]">{section.updated}</MonoLabel>
            </div>
            <div className="mt-6">
              <Blocks blocks={section.blocks} />
            </div>
            <div className="mt-10 flex items-center justify-between border-t border-line pt-5">
              {DOCS.indexOf(section) > 0 && (
                <button
                  data-testid="docs-prev"
                  onClick={() => setActive(DOCS[DOCS.indexOf(section) - 1].id)}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim transition-colors duration-150 hover:text-cyan"
                >
                  ← {DOCS[DOCS.indexOf(section) - 1].label}
                </button>
              )}
              <span />
              {DOCS.indexOf(section) < DOCS.length - 1 && (
                <button
                  data-testid="docs-next"
                  onClick={() => setActive(DOCS[DOCS.indexOf(section) + 1].id)}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim transition-colors duration-150 hover:text-cyan"
                >
                  {DOCS[DOCS.indexOf(section) + 1].label} →
                </button>
              )}
            </div>
          </article>
        </div>
      </section>
    </PageShell>
  );
}
